// Impact — "what GramFather has done for you". Growth since your first scan, the conversations it
// started, and the hours of manual Instagram work it has taken off your plate, priced at what YOUR
// time is worth. One Instagram account, or every account you run added together.
//
// Import as `@gf/shared/features/impact`. Owned by the analytics feature (routes/analytics/impact.ts).
//
// ── Endpoints (under /api/analytics; errors { error, code }) ─────────────────
//   GET /impact?igAccountId=            → ImpactReport   (no igAccountId = all of the user's accounts)
//   PUT /impact/rate { hourlyRate }     → ImpactSettings (whole dollars 1–10,000; one rate per login,
//                                                         stored as the user-level setting "impact")
//
// ── How the numbers are defined ──────────────────────────────────────────────
//   startedAt      the day the account was connected to GramFather (ig_accounts.created_at).
//   firstScanAt    the first follower count GramFather observed: the earliest account snapshot or
//                  completed follower scan. "Then" = that observation; "now" = the latest one.
//                  following/posts come from the first and latest daily snapshots.
//   growth         now − then; growthPct = growth / then (null when "then" was 0).
//   followersPerDay  growth / days between the two observations (null under one day apart).
//   engagement     average likes + comments per post: your last ENGAGEMENT_BEFORE_SAMPLE posts
//                  before startedAt vs every post since that is at least ENGAGEMENT_MIN_AGE_MS old
//                  (a brand-new post hasn't collected its likes yet). One account only.
//   messages       DMs sent (dm_out, every feature + by hand), distinct people messaged, and how
//                  many of them wrote back afterwards.
//   time saved     the work GramFather did FOR you (automation features only — nothing you did by
//                  hand counts), timed as a person doing the same work on their phone:
//                    every task     count × TIME_SAVED_TASKS[task].seconds (the looking, reading and
//                                   deciding around it)
//                    + writing      words GramFather actually wrote (DMs, comments, replies), typed
//                                   at PHONE_WORDS_PER_MINUTE; TIME_SAVED_TASKS[task].words each
//                                   where the text wasn't recorded
//                    + personal     PERSONALIZE_SECONDS per first DM written from their profile
//                                   (AI-personalized outreach steps)
//                  Campaign work counts too: every profile checked against your filters (kept or
//                  turned away), every lead tracked through follow-back and reply, every new
//                  follower traced to what earned it. App-switching and distraction aren't counted.
//                  taskSeconds() is the one formula; the portal shows the full breakdown.
//   value          hours saved × your hourly rate (default DEFAULT_HOURLY_RATE).
//   pace           time saved in the last 30 days (or since you started, if sooner), per 30 days.
//
// All accounts: growth adds up the accounts that have a first scan (then and now from the SAME
// accounts, so a just-connected account never reads as growth); time and messages add up everything.

import type { ID, Millis } from "../types";

// ── Your time ────────────────────────────────────────────────────────────────

/** What an online entrepreneur / creator in the US values an hour at, at the very least. */
export const DEFAULT_HOURLY_RATE = 25;
export const MIN_HOURLY_RATE = 1;
export const MAX_HOURLY_RATE = 10_000;
/** Quick picks in the portal. */
export const HOURLY_RATE_PRESETS: readonly number[] = [25, 50, 100, 250, 500];

/** settings.impact (user-level — one rate per person, whichever account they look at). */
export interface ImpactSettings {
  /** Whole US dollars per hour. */
  hourlyRate: number;
}

export const DEFAULT_IMPACT_SETTINGS: ImpactSettings = { hourlyRate: DEFAULT_HOURLY_RATE };

export function clampHourlyRate(v: unknown): number {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v.replace(/[$,\s]/g, "")) : NaN;
  if (!Number.isFinite(n)) return DEFAULT_HOURLY_RATE;
  return Math.min(MAX_HOURLY_RATE, Math.max(MIN_HOURLY_RATE, Math.round(n)));
}

/** Stored JSON (anything) → a valid ImpactSettings. */
export function resolveImpactSettings(raw: unknown): ImpactSettings {
  const src = typeof raw === "object" && raw !== null && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return { hourlyRate: src.hourlyRate === undefined ? DEFAULT_HOURLY_RATE : clampHourlyRate(src.hourlyRate) };
}

// ── Time saved ───────────────────────────────────────────────────────────────

export type TimeSavedTask =
  | "dm"
  | "dm_reply"
  | "lead_check"
  | "lead_tracking"
  | "comment"
  | "comment_reply"
  | "follow"
  | "like"
  | "story_view"
  | "unfollow"
  | "post"
  | "caption"
  | "tag"
  | "follower_check"
  | "attribution"
  | "research"
  | "profile_read"
  | "stats";

export interface TimeSavedRate {
  /** "DMs to new people" — plural noun phrase for the breakdown. */
  label: string;
  /** Seconds one takes a person by hand on their phone — for writing tasks, everything but the typing. */
  seconds: number;
  /**
   * Writing tasks only: typing comes on top, timed from the words GramFather actually wrote at
   * PHONE_WORDS_PER_MINUTE. This many words each when the text wasn't recorded.
   */
  words?: number;
  /** What that time covers (shown in "How we count your time"). */
  byHand: string;
}

/**
 * Average phone typing speed: 36 words a minute, copying ready-made text (Aalto University /
 * University of Cambridge, 37,000 people, 2019). Writing your own words is slower — the thinking
 * time in each writing task's `seconds` covers that.
 */
export const PHONE_WORDS_PER_MINUTE = 36;

/** Reading someone's bio and recent posts to find something real to say: per AI-personalized first DM. */
export const PERSONALIZE_SECONDS = 45;

/**
 * By-hand times, built from what a person actually does on their phone. Campaign work is split so
 * nothing counts twice: checking whether someone is a fit is `lead_check` (every profile checked,
 * kept or turned away), so a follow is just the tap.
 */
export const TIME_SAVED_TASKS: Record<TimeSavedTask, TimeSavedRate> = {
  dm: {
    label: "DMs to new people",
    seconds: 30,
    words: 30,
    byHand: "Find them again, open a new chat, decide what to say, type it out and send. Personal openers also mean reading their bio and posts first",
  },
  dm_reply: { label: "DM replies and follow-ups", seconds: 30, words: 20, byHand: "Open the chat, read where it's at, decide what to say, type it and send" },
  lead_check: {
    label: "Profiles checked for your campaigns",
    seconds: 30,
    byHand: "Scroll a list for people in your niche, open each profile and check it's a real, active fit — whether you keep them or not",
  },
  lead_tracking: {
    label: "Leads tracked through to a reply",
    seconds: 30,
    byHand: "Note who you reached out to and when, go back to see if they followed back, and stop following up once they answer",
  },
  comment: { label: "Comments on other people's posts", seconds: 35, words: 12, byHand: "Open the post, look at it and read the caption, think of something worth saying, type it" },
  comment_reply: { label: "Replies to comments on your posts", seconds: 20, words: 10, byHand: "Read the comment, decide how to answer, type the reply" },
  follow: { label: "Follows", seconds: 5, byHand: "Tap follow on a profile you've already checked" },
  like: { label: "Likes", seconds: 8, byHand: "Open one of their posts and double-tap" },
  story_view: { label: "Story views", seconds: 10, byHand: "Open their story and watch it" },
  unfollow: { label: "Unfollows", seconds: 10, byHand: "Open the profile of someone a campaign followed, check whether they follow you back, and unfollow" },
  post: { label: "Posts published on schedule", seconds: 300, byHand: "Be there at the right time, upload, caption, hashtags, publish" },
  caption: { label: "Captions and hashtags written", seconds: 300, byHand: "Write a caption in your voice and research hashtags for the post" },
  tag: { label: "Contacts sorted and tagged", seconds: 30, byHand: "Read their bio and file them in your CRM" },
  follower_check: { label: "New followers and unfollowers spotted", seconds: 15, byHand: "Compare your follower list by hand to see who came and who left" },
  attribution: {
    label: "New followers traced to what won them",
    seconds: 20,
    byHand: "Check a new follower against everyone you followed, liked and messaged to see what brought them in",
  },
  research: { label: "Influencers researched", seconds: 120, byHand: "Find a key account in your niche and size up its profile and posts" },
  profile_read: {
    label: "Profiles studied before a first DM",
    seconds: 90,
    byHand: "Scroll their profile, open their latest posts, read the captions and the text on the images, and pick something real to bring up",
  },
  stats: { label: "Days of growth stats tracked", seconds: 180, byHand: "Check and log your followers, posts and engagement every day" },
};

/** Display order of the breakdown when two lines tie. */
export const TIME_SAVED_ORDER: readonly TimeSavedTask[] = Object.keys(TIME_SAVED_TASKS) as TimeSavedTask[];

/** Features whose activities are GramFather working for you (manual + overlay are you). */
export const TIME_SAVED_FEATURES = ["autoresponder", "outreach", "engage", "comments", "scheduler", "spotlight", "autocaption", "autotag"] as const;

/**
 * Activity type → task for the one-rate tasks (only counted for TIME_SAVED_FEATURES).
 * post_edited "restore" doesn't count. Messages are timed by what was written: TIME_SAVED_MESSAGES.
 */
export const TIME_SAVED_ACTIVITY: Readonly<Record<string, TimeSavedTask>> = {
  follow: "follow",
  like: "like",
  story_view: "story_view",
  unfollow: "unfollow",
  post_published: "post",
  post_edited: "caption",
  contact_tagged: "tag",
};

/**
 * Message activities (text in meta.text). dm_out is "dm" when it opens a conversation (outreach
 * step 0) and "dm_reply" otherwise (follow-ups, auto-replies, DMs answering a comment).
 */
export const TIME_SAVED_MESSAGES = ["dm_out", "comment_out", "comment_reply"] as const;

/** One task's tally: how many, plus what the writing/personal parts need. */
export interface TaskTally {
  count: number;
  /** Words written across them; undefined = count × the task's typical `words`. */
  words?: number;
  /** "dm" only: how many were personalized from the person's profile. */
  personal?: number;
}

/** Typing time for this many words on a phone. */
export function typingSeconds(words: number): number {
  return (words / PHONE_WORDS_PER_MINUTE) * 60;
}

/** The one formula: what a tally of one task is worth in seconds by hand (whole seconds). */
export function taskSeconds(task: TimeSavedTask, t: TaskTally): number {
  if (t.count <= 0) return 0;
  const rate = TIME_SAVED_TASKS[task];
  let s = t.count * rate.seconds;
  if (rate.words !== undefined) s += typingSeconds(t.words ?? t.count * rate.words);
  if (task === "dm") s += Math.min(t.personal ?? 0, t.count) * PERSONALIZE_SECONDS;
  return Math.round(s);
}

/** Time-saved window for the monthly pace. */
export const PACE_WINDOW_DAYS = 30;

// ── Engagement before/after ──────────────────────────────────────────────────

export const ENGAGEMENT_BEFORE_SAMPLE = 12;
export const ENGAGEMENT_MIN_AGE_MS = 48 * 3_600_000;
/** Below these sample sizes the comparison is "still collecting". */
export const ENGAGEMENT_MIN_BEFORE = 3;
export const ENGAGEMENT_MIN_SINCE = 2;

// ── Report ───────────────────────────────────────────────────────────────────

export interface ImpactCounts {
  followers: number | null;
  following: number | null;
  posts: number | null;
  /** When the follower count was observed. */
  at: Millis | null;
}

export interface ImpactGrowth {
  /** At the first scan. */
  then: ImpactCounts;
  now: ImpactCounts;
  followerGrowth: number | null;
  /** 0.25 = +25%. null when "then" was 0 or unknown. */
  followerGrowthPct: number | null;
  followersPerDay: number | null;
  postsAdded: number | null;
}

export interface ImpactTimeLine {
  task: TimeSavedTask;
  count: number;
  /** taskSeconds() of this line. */
  seconds: number;
  /** Writing tasks: words written (recorded text, typical length where it wasn't). */
  words?: number;
  /** "dm": how many were personalized from the person's profile. */
  personal?: number;
}

export interface ImpactTime {
  totalSeconds: number;
  /** Tasks GramFather did for you. */
  actions: number;
  /** Count > 0 only, most time first. */
  lines: ImpactTimeLine[];
  /** Saved in the last PACE_WINDOW_DAYS (or since you started). */
  recentSeconds: number;
  recentDays: number;
  /** recentSeconds scaled to 30 days; null until you've used GramFather for a full day. */
  paceSecondsPer30d: number | null;
}

export interface ImpactEngagement {
  /** Avg likes + comments per post. */
  before: number | null;
  since: number | null;
  beforePosts: number;
  sincePosts: number;
  /** since / before − 1; null while either side is below its minimum sample. */
  liftPct: number | null;
}

export interface ImpactMessages {
  sent: number;
  peopleMessaged: number;
  replied: number;
  /** replied / peopleMessaged, 0–1. */
  replyRate: number | null;
}

export interface ImpactAccountSummary {
  igAccountId: ID;
  username: string;
  fullName: string;
  profilePicUrl: string;
  startedAt: Millis;
  firstScanAt: Millis | null;
  growth: ImpactGrowth;
  timeSavedSeconds: number;
  messagesSent: number;
}

export interface ImpactReport {
  scope: "account" | "all";
  /** The account (scope "account"), else null. */
  igAccountId: ID | null;
  accountCount: number;
  /** Accounts that have a first scan (= are in the growth numbers). */
  accountsWithBaseline: number;
  generatedAt: Millis;
  hourlyRate: number;
  /** Still on the default rate — the portal nudges "what's your hour worth?". */
  isDefaultRate: boolean;
  /** Earliest connection among the accounts. */
  startedAt: Millis | null;
  firstScanAt: Millis | null;
  /** Whole days since startedAt. */
  daysUsing: number;
  growth: ImpactGrowth;
  /** One account only (posts from different-sized accounts don't average meaningfully). */
  engagement: ImpactEngagement | null;
  messages: ImpactMessages;
  time: ImpactTime;
  /** Every account in the scope, newest connection last. */
  accounts: ImpactAccountSummary[];
}

// ── Pure helpers (API + portal) ──────────────────────────────────────────────

const DAY_MS = 86_400_000;

/** then → now growth. Pass counts from the same set of accounts. */
export function growthBetween(then: ImpactCounts, now: ImpactCounts): ImpactGrowth {
  const followerGrowth = then.followers !== null && now.followers !== null ? now.followers - then.followers : null;
  const followerGrowthPct = followerGrowth !== null && then.followers ? Math.round((followerGrowth / then.followers) * 10_000) / 10_000 : null;
  const span = then.at !== null && now.at !== null ? (now.at - then.at) / DAY_MS : 0;
  const followersPerDay = followerGrowth !== null && span >= 1 ? Math.round((followerGrowth / span) * 10) / 10 : null;
  const postsAdded = then.posts !== null && now.posts !== null ? now.posts - then.posts : null;
  return { then, now, followerGrowth, followerGrowthPct, followersPerDay, postsAdded };
}

/** Add up per-account growth (accounts without a first scan are left out of both sides). */
export function sumGrowth(items: readonly ImpactGrowth[]): ImpactGrowth {
  const known = items.filter((g) => g.then.followers !== null && g.now.followers !== null);
  const add = (pick: (g: ImpactGrowth) => number | null) => {
    const vals = known.map(pick);
    return vals.every((v) => v !== null) && vals.length ? (vals as number[]).reduce((a, b) => a + b, 0) : null;
  };
  const minAt = (pick: (g: ImpactGrowth) => number | null) => {
    const vals = known.map(pick).filter((v): v is number => v !== null);
    return vals.length ? Math.min(...vals) : null;
  };
  const maxAt = (pick: (g: ImpactGrowth) => number | null) => {
    const vals = known.map(pick).filter((v): v is number => v !== null);
    return vals.length ? Math.max(...vals) : null;
  };
  const then: ImpactCounts = { followers: add((g) => g.then.followers), following: add((g) => g.then.following), posts: add((g) => g.then.posts), at: minAt((g) => g.then.at) };
  const now: ImpactCounts = { followers: add((g) => g.now.followers), following: add((g) => g.now.following), posts: add((g) => g.now.posts), at: maxAt((g) => g.now.at) };
  const g = growthBetween(then, now);
  // Followers per day across accounts = the sum of each account's own daily rate.
  const rates = known.map((x) => x.followersPerDay).filter((v): v is number => v !== null);
  return { ...g, followersPerDay: rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) * 10) / 10 : null };
}

/** A task's tally all-time, plus the same for the pace window. */
export interface TaskCounts extends TaskTally {
  recent: number;
  recentWords?: number;
  recentPersonal?: number;
}

/** Add two tallies of the same task (words stay undefined only when both are). */
export function addTaskCounts(a: TaskCounts, b: TaskCounts): TaskCounts {
  const sum = (x: number | undefined, y: number | undefined) => (x === undefined && y === undefined ? undefined : (x ?? 0) + (y ?? 0));
  return {
    count: a.count + b.count,
    recent: a.recent + b.recent,
    words: sum(a.words, b.words),
    recentWords: sum(a.recentWords, b.recentWords),
    personal: sum(a.personal, b.personal),
    recentPersonal: sum(a.recentPersonal, b.recentPersonal),
  };
}

/** Task counts (all-time + recent) → the time-saved block. */
export function timeSaved(counts: ReadonlyMap<TimeSavedTask, TaskCounts>, recentDays: number, daysUsing: number): ImpactTime {
  const lines: ImpactTimeLine[] = [];
  let totalSeconds = 0;
  let actions = 0;
  let recentSeconds = 0;
  for (const task of TIME_SAVED_ORDER) {
    const c = counts.get(task);
    if (!c || c.count <= 0) continue;
    const rate = TIME_SAVED_TASKS[task];
    const seconds = taskSeconds(task, c);
    const line: ImpactTimeLine = { task, count: c.count, seconds };
    if (rate.words !== undefined) line.words = Math.round(c.words ?? c.count * rate.words);
    if (task === "dm") line.personal = Math.min(c.personal ?? 0, c.count);
    lines.push(line);
    totalSeconds += seconds;
    actions += c.count;
    recentSeconds += taskSeconds(task, { count: Math.max(0, c.recent), words: c.recentWords, personal: c.recentPersonal });
  }
  lines.sort((a, b) => b.seconds - a.seconds || TIME_SAVED_ORDER.indexOf(a.task) - TIME_SAVED_ORDER.indexOf(b.task));
  const paceSecondsPer30d = daysUsing >= 1 && recentDays > 0 ? Math.round((recentSeconds / recentDays) * PACE_WINDOW_DAYS) : null;
  return { totalSeconds, actions, lines, recentSeconds, recentDays, paceSecondsPer30d };
}

/** Dollars your saved time is worth (unrounded). */
export function timeValue(seconds: number, hourlyRate: number): number {
  return (seconds / 3600) * hourlyRate;
}

/** Engagement comparison with the minimum-sample rule applied. */
export function engagementLift(e: Omit<ImpactEngagement, "liftPct">): ImpactEngagement {
  const ok = e.beforePosts >= ENGAGEMENT_MIN_BEFORE && e.sincePosts >= ENGAGEMENT_MIN_SINCE && e.before !== null && e.since !== null && e.before > 0;
  return { ...e, liftPct: ok ? Math.round((e.since! / e.before! - 1) * 10_000) / 10_000 : null };
}
